/**
 * StyledTextField Field Tracking Hook
 *
 * Handles real-time field update tracking and visual indicators:
 * - Monitors external field updates (Firebase, system, AI, etc.)
 * - Provides visual feedback for recent changes
 * - Integrates with FieldUpdateContext for collaborative features
 * - Tracks field value changes and reports to parent components
 */

import { useMemo, useEffect, useRef, useContext } from 'react';
import { FieldUpdateContext } from '../../../contexts/FieldUpdateContext';
import type {
  StyledTextFieldProps,
  FieldTrackingHookResult,
  FieldUpdateInfo
} from './StyledTextField.types';

// ============================================================================
// FIELD TRACKING HOOK
// ============================================================================

export const useFieldTracking = (props: StyledTextFieldProps): FieldTrackingHookResult => {
  const {
    fieldPath,
    trackFieldUpdates = false,
    showUpdateIndicator = false,
    onFieldUpdate,
    value
  } = props;

  // Access field update context (optional)
  const fieldUpdateContext = useContext(FieldUpdateContext);

  // Ref to track previous value for change detection
  const previousValueRef = useRef<string>(value);

  // ============================================================================
  // FIELD UPDATE DETECTION
  // ============================================================================

  /**
   * Get information about recent updates to this field
   */
  const fieldUpdateInfo = useMemo((): FieldUpdateInfo | null => {
    if (!fieldUpdateContext || !fieldPath) return null;

    const now = Date.now();
    const tenSecondsAgo = now - (10 * 1000);

    // Get most recent update for this field (excluding user's own changes)
    const recentUpdate = fieldUpdateContext.fieldUpdates
      .filter(update =>
        update.path === fieldPath &&
        update.timestamp > tenSecondsAgo &&
        update.source !== 'user' // Don't show indicators for user's own changes
      )
      .sort((a, b) => b.timestamp - a.timestamp)[0];

    // Convert FieldUpdateEvent to FieldUpdateInfo if found
    if (recentUpdate) {
      return {
        path: recentUpdate.path,
        timestamp: recentUpdate.timestamp,
        source: recentUpdate.source,
        value: recentUpdate.newValue,
        oldValue: recentUpdate.oldValue,
        userId: recentUpdate.metadata?.userId
      };
    }

    return null;
  }, [fieldUpdateContext?.fieldUpdates, fieldPath]);

  /**
   * Check if field has recent updates
   */
  const hasRecentUpdate = fieldUpdateInfo !== null && showUpdateIndicator;

  /**
   * Get update source for styling
   */
  const updateSource = fieldUpdateInfo?.source || 'user';

  // ============================================================================
  // VALUE CHANGE TRACKING
  // ============================================================================

  /**
   * Track value changes and report to context/parent
   */
  useEffect(() => {
    if (!trackFieldUpdates || !fieldPath || !onFieldUpdate) {
      return;
    }

    // Only report if value actually changed
    if (previousValueRef.current !== value) {
      onFieldUpdate(fieldPath, value);
      previousValueRef.current = value;
    }
  }, [value, fieldPath, trackFieldUpdates, onFieldUpdate]);

  // ============================================================================
  // TRACKING UTILITIES
  // ============================================================================

  /**
   * Get all updates for this field (not just recent ones)
   */
  const getAllFieldUpdates = useMemo((): FieldUpdateInfo[] => {
    if (!fieldUpdateContext || !fieldPath) return [];

    return fieldUpdateContext.fieldUpdates
      .filter(update => update.path === fieldPath)
      .sort((a, b) => b.timestamp - a.timestamp)
      .map(update => ({
        path: update.path,
        timestamp: update.timestamp,
        source: update.source,
        value: update.newValue,
        oldValue: update.oldValue,
        userId: update.metadata?.userId
      }));
  }, [fieldUpdateContext?.fieldUpdates, fieldPath]);

  /**
   * Get update count for this field
   */
  const getUpdateCount = useMemo(() => {
    return getAllFieldUpdates.length;
  }, [getAllFieldUpdates]);

  /**
   * Check if field has been modified by external sources
   */
  const hasExternalModifications = useMemo(() => {
    return getAllFieldUpdates.some(update => update.source !== 'user');
  }, [getAllFieldUpdates]);

  /**
   * Get the most recent external update timestamp
   */
  const getLastExternalUpdate = useMemo(() => {
    const externalUpdates = getAllFieldUpdates.filter(update => update.source !== 'user');
    return externalUpdates.length > 0 ? externalUpdates[0].timestamp : null;
  }, [getAllFieldUpdates]);

  // ============================================================================
  // RETURN HOOK RESULT
  // ============================================================================

  return {
    hasRecentUpdate,
    fieldUpdateInfo,
    updateSource,
    getAllFieldUpdates,
    getUpdateCount,
    hasExternalModifications,
    getLastExternalUpdate,
  };
};

// ============================================================================
// UPDATE INDICATOR UTILITIES
// ============================================================================

/**
 * Get time-based indicator intensity
 * More recent updates have higher intensity
 */
export const getUpdateIntensity = (timestamp: number): number => {
  const now = Date.now();
  const secondsAgo = (now - timestamp) / 1000;

  if (secondsAgo < 2) return 1.0;     // 100% intensity for very recent
  if (secondsAgo < 5) return 0.8;     // 80% intensity for recent
  if (secondsAgo < 10) return 0.6;    // 60% intensity for moderately recent
  if (secondsAgo < 30) return 0.4;    // 40% intensity for older
  return 0.2;                         // 20% intensity for old
};

/**
 * Get human-readable time description
 */
export const getUpdateTimeDescription = (timestamp: number): string => {
  const now = Date.now();
  const secondsAgo = (now - timestamp) / 1000;

  if (secondsAgo < 5) return 'just now';
  if (secondsAgo < 60) return `${Math.floor(secondsAgo)} seconds ago`;
  if (secondsAgo < 3600) return `${Math.floor(secondsAgo / 60)} minutes ago`;
  if (secondsAgo < 86400) return `${Math.floor(secondsAgo / 3600)} hours ago`;
  return `${Math.floor(secondsAgo / 86400)} days ago`;
};

/**
 * Get update source display name
 */
export const getUpdateSourceDisplayName = (source: string): string => {
  switch (source) {
    case 'firebase':
      return 'Remote Update';
    case 'system':
      return 'System Update';
    case 'ai':
      return 'AI Update';
    case 'user':
      return 'User Update';
    default:
      return 'Unknown Update';
  }
};

// ============================================================================
// FIELD UPDATE CONTEXT HELPERS
// ============================================================================

/**
 * Check if field update context is available
 */
export const isFieldTrackingAvailable = (context: any): boolean => {
  return context && typeof context.fieldUpdates === 'object' && Array.isArray(context.fieldUpdates);
};

/**
 * Safe field update context access
 */
export const useFieldUpdateContextSafe = () => {
  const context = useContext(FieldUpdateContext);
  return isFieldTrackingAvailable(context) ? context : null;
};